import { Button, Card, Image, makeStyles, mergeClasses, Persona, Skeleton, SkeletonItem, Text, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import blogService from "../../services/BlogService";
import { Link } from "react-router-dom";
import Status from "../../enums/Status";
import ITeamMember from "../../models/ITeamMember";
import { Avatar } from "../../components/avatar/Avatar";
import { dolphinTokens } from "../../utils/Theme";
import IEntryPreivew from "../../models/blog/IEntryPreview";

const placeholderSizes = [128, 24, 20, 32] as const;

const useStyles = makeStyles({
    contentWrapper: {
        paddingBottom: "3rem"
    },
    row: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap'
    },
    blueBadge: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: 'white',
        padding: '0.5rem 1.5rem',
        borderRadius: '50px',
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        display: 'inline-block',
    },
    sectionHeader: {
        textAlign: 'center',
        width: '100%'
    },
    title: {
        marginBottom: 0
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    cardCol: {
        maxWidth: '350px',
        width: '100%',
        flex: '1 1 350px',
        margin: '1rem',
    },
    cardImage: {
        width: '100%',
        height: '150px',
        objectFit: 'cover',
    },
    cardTitle: {
        marginTop: '8px',
        marginBottom: 0,
    },
    cardDescription: {
        marginTop: 0,
        marginBottom: '4px',
        color: '#555',
    },
    persona: {
        marginTop: 'auto'
    },
    cardButton: {
        display: 'block',
        width: '100%',

        "& button": {
            width: "100%"
        }
    },
    skeletons: {
        flexDirection: 'column',
        gap: tokens.spacingVerticalL
    }
});

const Placeholder = () => {
    const styles = useStyles();
    const cardClassName = mergeClasses(styles.card, styles.skeletons);

    return (
        <div className={styles.cardCol}>
            <Card className={cardClassName}>
                {
                    placeholderSizes.map((size) => (
                        <Skeleton key={size}>
                            <SkeletonItem size={size} shape="rectangle" />
                        </Skeleton>
                    ))
                }
            </Card>
        </div>
    );
};

const BlogEntry = (props: { blog: IEntryPreivew }) => {
    const { blog } = props;
    const { t } = useTranslation(["home", "blog"]);
    const styles = useStyles();

    const person: ITeamMember = {
        id: blog.authorId!,
        name: blog.author!,
        position: ""
    };

    const publishedDate = new Date(blog.published);
    const publishedStr = publishedDate.toDateString();

    const readTime = blog.readTime
        ? blog.readTime === 1
            ? t("readTime.one", { ns: "blog" })
            : t("readTime.other", { count: blog.readTime, ns: "blog" })
        : "";

    return (
        <div className={styles.cardCol}>
            <Card className={styles.card}>
                <Image
                    src={blog.thumbnail.url}
                    alt={blog.thumbnail.altText}
                    className={styles.cardImage}
                />
                <Text className={styles.cardTitle} size={400} weight="semibold">
                    {blog.title.title}
                </Text>
                <Text className={styles.cardDescription} size={300}>
                    {readTime}
                </Text>
                <Persona
                    name={blog.author}
                    avatar={Avatar(person)}
                    secondaryText={publishedStr}
                    className={styles.persona}
                    />
                <Link to={`/blog/${blog.slug}`} className={styles.cardButton}>
                    <Button appearance="secondary">
                        {t('blog.btnReadMore')}
                    </Button>
                </Link>
            </Card>
        </div>
    );
};

export default function Blog() {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [blogs, setBlogPosts] = useState<IEntryPreivew[]>([]);

    useEffect(() => {
        getBlog(setBlogPosts, setStatus);
    }, []);

    if (status === Status.LOAD_ERROR) {
        return null;
    }

    return (
        <div className={styles.contentWrapper}>
            <div className={styles.row}>
                <div className={styles.sectionHeader}>
                    <Text as="span" className={styles.blueBadge}>
                        {t('blog.pill')}
                    </Text>
                </div>
                <Text block as="h2" size={700} align="center" className={styles.title}>
                    {t('blog.title')}
                </Text>
            </div>

            {
                status === Status.LOADING &&
                <div className={styles.row}>
                    {
                        Array.from({ length: 3 }, (_, index) => <Placeholder key={index} />)
                    }
                </div>
            }

            {
                status === Status.LOAD_SUCCESS &&
                <div className={styles.row}>
                    {
                        blogs.slice(0, 3).map((blog) => <BlogEntry key={blog.id} blog={blog} />)
                    }
                </div>
            }
        </div>
    );
}

async function getBlog(
    setBlogPosts: (blogPosts: IEntryPreivew[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const response = await blogService.getBlogPreviews();

        setBlogPosts(response);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}